'use client'

import type { ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'

import { Avatar, AvatarFallback } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

interface CheckoutCardProps {
  /** The buying organization, named at the top so the card says whose order this is. */
  organizationName: string
  /** "Premium", "Perpanjang Premium", "Tambah kursi". */
  title: string
  /** The headline rate — the big line under the title. */
  primaryLine?: ReactNode
  /** The per-month rate, or whatever qualifies the headline. */
  secondaryLine?: ReactNode
  /** One more line under those, for a fact the buyer needs before the form. */
  metaLine?: ReactNode
  children: ReactNode
}

/**
 * The order card the three checkouts share.
 *
 * Extracted because they had drifted into three hand-maintained copies of the same shell, and the
 * flows are meant to read identically whether a school is buying, renewing or topping up.
 *
 * The card itself does not animate in. The figures inside it do (see `AnimatedCurrency`), and a
 * shell sliding up underneath numbers that are already rolling was two motions competing for the
 * same glance.
 */
export function CheckoutCard({
  organizationName,
  title,
  primaryLine,
  secondaryLine,
  metaLine,
  children,
}: CheckoutCardProps) {
  return (
    <section
      className={cn(
        'srd:bg-neutral-white srd:relative srd:flex srd:w-full srd:flex-col srd:gap-y-4 srd:overflow-hidden srd:rounded-2xl srd:p-6',
        'srd:border-outline-variant/40 srd:border srd:shadow-sm',
      )}
    >
      {/* A 3px accent along the top edge. The card used to be a plain white rectangle among other
          white rectangles; this is the one mark that says which one is the order. */}
      <span aria-hidden className='srd:bg-gradient-orange-red srd:absolute srd:inset-x-0 srd:top-0 srd:h-[3px]' />

      <section className='srd:flex srd:items-center srd:gap-x-2'>
        <Avatar profile='organization' size='sm'>
          <AvatarFallback>{getInitials(organizationName)}</AvatarFallback>
        </Avatar>
        <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>{organizationName}</p>
      </section>

      <section className='srd:flex srd:flex-col srd:gap-y-1'>
        <h2 className={cn(typographyVariants({ variant: 'title-lg' }), 'srd:text-neutral-black')}>{title}</h2>

        {(primaryLine || secondaryLine) && (
          <section className='srd:flex srd:flex-col'>
            {primaryLine && (
              <p className={cn(typographyVariants({ variant: 'label-lg' }), 'srd:text-neutral-black')}>{primaryLine}</p>
            )}

            {secondaryLine && (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-purple-100')}>{secondaryLine}</p>
            )}
          </section>
        )}

        {metaLine && <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>{metaLine}</p>}
      </section>

      {children}
    </section>
  )
}

/**
 * A row that fades in when it becomes relevant and out when it stops.
 *
 * The savings lines and the promo discount appear and disappear as the buyer moves the stepper, and
 * popping in and out mid-column was the jumpiest thing on the card. Height is animated too, so the
 * rows below settle rather than jump.
 */
export function CheckoutReveal({ show, children }: { show: boolean; children: ReactNode }) {
  const reduceMotion = useReducedMotion()

  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className='srd:overflow-hidden'
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
